import React, { useRef, useState } from "react";
import { Upload, X, Plus, Trash2, Loader2, Link as LinkIcon } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export const getPath = (obj, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
export const setPath = (obj, path, val) => {
  const keys = path.split(".");
  const out = { ...obj };
  let cur = out;
  keys.forEach((k, i) => {
    if (i === keys.length - 1) cur[k] = val;
    else { cur[k] = { ...(cur[k] || {}) }; cur = cur[k]; }
  });
  return out;
};

export const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-jannah-sand bg-white text-sm text-jannah-ink focus:outline-none focus:ring-2 focus:ring-jannah-green/40";
const labelCls = "block text-xs font-semibold uppercase tracking-wide text-jannah-ink/60 mb-1.5";

export const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
};

export const ImageField = ({ value, onChange, testid }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [urlMode, setUrlMode] = useState(false);
  const base = process.env.REACT_APP_BACKEND_URL;
  const preview = value ? (value.startsWith("/api/") ? base + value : value) : "";

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { onChange(await uploadFile(file)); toast.success("Image uploaded"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <div className="flex gap-4 items-start" data-testid={testid}>
      <div className="w-32 h-24 rounded-lg border border-dashed border-jannah-sand bg-jannah-cream overflow-hidden flex items-center justify-center shrink-0">
        {preview ? <img src={preview} alt="" className="w-full h-full object-cover" /> : <Upload size={20} className="text-jannah-ink/30" />}
      </div>
      <div className="flex-1 space-y-2">
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => ref.current.click()} disabled={busy} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-jannah-green text-white text-xs font-semibold hover:bg-jannah-green-dark disabled:opacity-60" data-testid={testid ? `${testid}-upload-btn` : undefined}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {busy ? "Uploading…" : "Upload image"}
          </button>
          <button type="button" onClick={() => setUrlMode((v) => !v)} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-jannah-sand text-xs font-semibold text-jannah-ink/70 hover:border-jannah-green"><LinkIcon size={14} /> Paste URL</button>
          {value && <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-xs font-semibold text-red-600 hover:bg-red-50"><X size={14} /> Remove</button>}
        </div>
        {urlMode && <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://…" className={inputCls} />}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={testid ? `${testid}-file-input` : undefined} />
      </div>
    </div>
  );
};

const ObjectsField = ({ field, value = [], onChange }) => (
  <div className="space-y-3">
    {value.map((row, i) => (
      <div key={i} className="rounded-lg border border-jannah-sand p-4 bg-jannah-cream/50 relative">
        <button type="button" onClick={() => onChange(value.filter((_, k) => k !== i))} className="absolute top-2 right-2 text-red-500 hover:bg-red-50 rounded p-1"><Trash2 size={14} /></button>
        <div className="grid sm:grid-cols-2 gap-3 pr-8">
          {field.fields.map((f) => (
            <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
              <label className={labelCls}>{f.label}</label>
              {f.type === "textarea"
                ? <textarea rows={2} value={row[f.key] || ""} onChange={(e) => onChange(value.map((r, k) => (k === i ? { ...r, [f.key]: e.target.value } : r)))} className={inputCls} />
                : <input value={row[f.key] || ""} onChange={(e) => onChange(value.map((r, k) => (k === i ? { ...r, [f.key]: e.target.value } : r)))} className={inputCls} />}
            </div>
          ))}
        </div>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...value, {}])} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-dashed border-jannah-green text-jannah-green text-xs font-semibold hover:bg-jannah-green/5"><Plus size={14} /> Add row</button>
  </div>
);

export const Field = ({ field, value, onChange, disabled }) => {
  const id = `field-${field.key.replace(/\./g, "-")}`;
  const common = { id, disabled, "data-testid": id };
  let control;
  switch (field.type) {
    case "textarea": control = <textarea {...common} rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />; break;
    case "number": control = <input {...common} type="number" step="any" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className={inputCls} />; break;
    case "date": control = <input {...common} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />; break;
    case "bool": control = <label className="inline-flex items-center gap-2 text-sm cursor-pointer"><input {...common} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-jannah-green" /> Yes</label>; break;
    case "select": control = <select {...common} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls}><option value="">—</option>{field.options.map((o) => <option key={o}>{o}</option>)}</select>; break;
    case "image": control = <ImageField value={value} onChange={onChange} testid={id} />; break;
    case "images": control = (
      <div className="space-y-3">
        {(value || []).map((v, i) => (
          <div key={i} className="flex items-start gap-2">
            <div className="flex-1"><ImageField value={v} onChange={(nv) => onChange(value.map((x, k) => (k === i ? nv : x)))} /></div>
            <button type="button" onClick={() => onChange(value.filter((_, k) => k !== i))} className="text-red-500 p-2 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...(value || []), ""])} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-dashed border-jannah-green text-jannah-green text-xs font-semibold"><Plus size={14} /> Add image</button>
      </div>
    ); break;
    case "lines": control = <textarea {...common} rows={5} value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n").filter((l) => l.trim() !== ""))} className={inputCls} placeholder="One item per line" />; break;
    case "objects": control = <ObjectsField field={field} value={value || []} onChange={onChange} />; break;
    default: control = <input {...common} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
  }
  return (
    <div className={["textarea", "lines", "objects", "image", "images"].includes(field.type) ? "sm:col-span-2" : ""}>
      <label htmlFor={id} className={labelCls}>{field.label}</label>
      {control}
      {field.hint && <p className="mt-1 text-xs text-jannah-ink/50">{field.hint}</p>}
    </div>
  );
};
